// Deterministic copy templates for Season Wrapped — THE FLOOR.
//
// Every prose field gets a template line generated from the locked numbers BEFORE
// any AI is involved. If the Claude narration call fails or times out, Wrapped
// still ships fully — just a little less poetic — and is never broken or empty.
// The AI layer (season-wrapped endpoint) overwrites these strings when it returns.
//
// Voice: warm, dugout, suggestive — a bench coach talking to the head coach. Never
// comparative across kids, never corrective. No em/en dashes in the copy (matches
// the rest of the app's bench-coach voice rule).

import type { SeasonWrappedData, WrappedSuperlative } from '../../types/dugout';

const recordLine = (w: number, l: number, t: number) =>
  t > 0 ? `${w}-${l}-${t}` : `${w}-${l}`;

/** The season thesis fallback — a real throughline built from the record shape. */
export function templateThesis(d: SeasonWrappedData): string {
  const { wins, losses, ties } = d.record;
  const games = wins + losses + ties;
  const rec = recordLine(wins, losses, ties);
  const oneRunW = parseInt(d.runDiff.oneRunRecord.split('-')[0] || '0');

  if (games === 0) return `${d.teamName}, your ${d.seasonYear} season is in the books.`;
  if (oneRunW >= 2 && wins >= losses) {
    return `${d.teamName} learned how to win the close ones. You finished ${rec}, and ${oneRunW} of those wins came down to a single run.`;
  }
  if (d.runDiff.diff > 0 && wins > losses) {
    return `${d.teamName} outscored the season by ${d.runDiff.diff}. A ${rec} record built on showing up, at-bat after at-bat.`;
  }
  if (wins >= losses) {
    return `A ${rec} season for ${d.teamName}. The kind of year that gets built one inning at a time.`;
  }
  return `${d.teamName} battled through a ${rec} season. Every game was a chance to get better, and they took it.`;
}

/** The warm closing sign-off fallback. */
export function templateClosing(d: SeasonWrappedData): string {
  return `That is your ${d.seasonYear} season, ${d.teamName}. Real games, real growth, real moments. See you next season.`;
}

const SUPERLATIVE_PROSE: Record<WrappedSuperlative['angle'], (s: WrappedSuperlative) => string> = {
  clutch: s => `When it mattered most, the bat found ${s.playerName}. ${s.statLine} when the game was on the line.`,
  leader: s => `${s.playerName} led the way with ${s.statLine}. A steady hand at the top of the chart all season.`,
  most_improved: s => `Nobody climbed further than ${s.playerName}. ${s.statLine} from the first half to the second. That is the development arc you love to see.`,
  workhorse: s => `${s.playerName} carried the load on the mound, logging ${s.statLine}. Whenever you needed outs, the ball found them.`,
  grinder: s => `${s.playerName} made pitchers work. ${s.statLine} this season, taking the walk and passing the baton.`,
  glove: s => `${s.playerName} made the plays behind the plate. ${s.statLine} that kept innings clean.`,
  game_balls: s => `${s.playerName} kept earning the nod. ${s.statLine} this season, the kind of stuff that does not always show in the box score.`,
  personal_best: s => `${s.playerName} had a day to remember: a ${s.statLine} that the team will not forget.`,
};

export function templateSuperlativeProse(s: WrappedSuperlative): string {
  return (SUPERLATIVE_PROSE[s.angle] || SUPERLATIVE_PROSE.personal_best)(s);
}

export function templateBiggestMomentCall(playerName: string, description: string): string {
  return `The moment of the season: ${playerName} with a ${description}. The dugout erupted.`;
}

export function templateClutchProse(playerName: string, statLine: string): string {
  return `${playerName} was the bat you wanted up in a big spot. ${statLine} across the season when the leverage was highest.`;
}

export function templateMoundProse(playerName: string, description: string): string {
  return `${playerName} answered the bell, ${description}. The kind of inning that wins close games.`;
}

/** Transition lines between slides (slot-keyed). Generic but on-voice. */
export const TRANSITIONS = {
  toRunDiff: 'But the record is only the half of it.',
  toHighlights: 'A few moments stood out from the rest.',
  toBatting: 'At the plate, some bats carried the load.',
  toPitching: 'And on the mound, arms got the job done.',
  toCoach: 'None of it happens without the decisions in the dugout.',
};
